import type { SurveyQuestion } from './question.js';

export interface SurveyAnswer {
  questionKey: string;
  raw?: unknown;
  text?: string;
  number?: number;
  choices?: string[];
  boolean?: boolean;
  extra?: unknown;
}

export function isAnswerEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).every(isAnswerEmpty);
  }
  return false;
}

export function packAnswer(
  questionKey: string,
  value: unknown,
  questions: SurveyQuestion[],
): SurveyAnswer {
  const question = questions.find((entry) => entry.key === questionKey);
  const base: SurveyAnswer = { questionKey, raw: value };
  if (!question) return base;

  switch (question.type) {
    case 'multipleChoice':
    case 'multiChoice':
    case 'checkboxes':
      return { ...base, choices: Array.isArray(value) ? (value as string[]) : [] };
    case 'singleChoice':
    case 'choice':
    case 'dropdown':
      return { ...base, text: typeof value === 'string' ? value : String(value) };
    case 'rating':
    case 'nps':
    case 'csat':
    case 'ces':
    case 'scale':
    case 'likert':
    case 'emojiScale':
    case 'number':
      return { ...base, number: typeof value === 'number' ? value : Number(value) };
    case 'yesNo':
      return { ...base, boolean: Boolean(value) };
    case 'maxdiff': {
      const maxDiff = value as { best?: unknown; worst?: unknown } | undefined;
      const choices = [maxDiff?.best, maxDiff?.worst]
        .filter((item) => item !== null && item !== undefined && item !== '')
        .map((item) => String(item));
      return { ...base, choices, extra: value };
    }
    default:
      return { ...base, text: typeof value === 'string' ? value : String(value ?? '') };
  }
}
